import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  WORKSPACES,
  type WorkspaceId,
  type WorkspaceNavItem,
} from '../constants/workspaces';

export interface RouteSearchResult {
  id: string;
  label: string;
  path: string;
  workspaceId: WorkspaceId;
  workspaceLabel: string;
  groupLabel: string;
  icon: WorkspaceNavItem['icon'];
}

interface RouteWorkspaceGroup {
  workspaceId: WorkspaceId;
  workspaceLabel: string;
  routes: RouteSearchResult[];
}

function collectRoutes(
  hasPermission: (permission: string) => boolean,
): RouteSearchResult[] {
  const results: RouteSearchResult[] = [];

  for (const workspace of WORKSPACES) {
    const overview = workspace.overview;
    if (!overview.requiredPermission || hasPermission(overview.requiredPermission)) {
      results.push({
        id: `${workspace.id}:${overview.id}`,
        label: overview.label === 'Overview' ? `${workspace.label} Overview` : overview.label,
        path: overview.path,
        workspaceId: workspace.id,
        workspaceLabel: workspace.label,
        groupLabel: 'Overview',
        icon: overview.icon,
      });
    }

    for (const group of workspace.groups) {
      for (const item of group.items) {
        if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
          continue;
        }
        results.push({
          id: `${workspace.id}:${item.id}`,
          label: item.label,
          path: item.path,
          workspaceId: workspace.id,
          workspaceLabel: workspace.label,
          groupLabel: group.label,
          icon: item.icon,
        });
      }
    }
  }

  return results;
}

function matchesQuery(route: RouteSearchResult, query: string): boolean {
  const haystack = `${route.label} ${route.groupLabel} ${route.workspaceLabel} ${route.path}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token));
}

/** When idle, show a balanced sample from every workspace so all roles are visible. */
function sampleAcrossWorkspaces(
  routes: RouteSearchResult[],
  perWorkspace = 4,
): RouteSearchResult[] {
  const byWorkspace = new Map<WorkspaceId, RouteSearchResult[]>();
  for (const route of routes) {
    const list = byWorkspace.get(route.workspaceId) ?? [];
    if (list.length < perWorkspace) {
      list.push(route);
      byWorkspace.set(route.workspaceId, list);
    }
  }

  const sampled: RouteSearchResult[] = [];
  for (const workspace of WORKSPACES) {
    const list = byWorkspace.get(workspace.id);
    if (list?.length) sampled.push(...list);
  }
  return sampled;
}

function groupByWorkspace(routes: RouteSearchResult[]): RouteWorkspaceGroup[] {
  const groups: RouteWorkspaceGroup[] = [];
  const indexById = new Map<WorkspaceId, number>();

  for (const route of routes) {
    const existing = indexById.get(route.workspaceId);
    if (existing === undefined) {
      indexById.set(route.workspaceId, groups.length);
      groups.push({
        workspaceId: route.workspaceId,
        workspaceLabel: route.workspaceLabel,
        routes: [route],
      });
    } else {
      groups[existing].routes.push(route);
    }
  }

  return groups;
}

interface RouteCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RouteCommandPalette: React.FC<RouteCommandPaletteProps> = ({
  open,
  onOpenChange,
}) => {
  const { hasPermission } = useAuth();
  const { setWorkspaceId, workspaceId } = useWorkspace();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const allRoutes = useMemo(() => collectRoutes(hasPermission), [hasPermission]);

  const filtered = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return sampleAcrossWorkspaces(allRoutes, 5);
    return allRoutes.filter((route) => matchesQuery(route, trimmed)).slice(0, 40);
  }, [allRoutes, query]);

  const grouped = useMemo(() => groupByWorkspace(filtered), [filtered]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const selectRoute = useCallback(
    (route: RouteSearchResult) => {
      setWorkspaceId(route.workspaceId);
      navigate(route.path);
      close();
    },
    [close, navigate, setWorkspaceId],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((current) => Math.min(current + 1, Math.max(filtered.length - 1, 0)));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((current) => Math.max(current - 1, 0));
        return;
      }
      if (event.key === 'Enter' && filtered[activeIndex]) {
        event.preventDefault();
        selectRoute(filtered[activeIndex]);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, close, filtered, open, selectRoute]);

  if (!open) return null;

  let flatIndex = -1;

  return (
    <div className="fixed inset-0 z-[10000] flex items-start justify-center px-4 pt-[12vh]">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        aria-label="Close search"
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search routes"
        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-border bg-background-secondary shadow-2xl shadow-black/20"
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <SearchOutlined className="text-foreground-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search across all workspaces…"
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
          />
          <kbd className="rounded-md bg-background px-1.5 py-0.5 text-[10px] font-medium text-foreground-muted ring-1 ring-border">
            esc
          </kbd>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-2 pb-2" role="listbox">
          {filtered.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-foreground-muted">
              No routes match “{query.trim()}”
            </p>
          )}

          {grouped.map((group, groupIndex) => (
            <div key={group.workspaceId} className={groupIndex === 0 ? '' : 'mt-1'}>
              <div className="sticky top-0 z-[1] flex items-center gap-2 bg-background-secondary px-2.5 py-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground-muted">
                  {group.workspaceLabel}
                </p>
                {group.workspaceId === workspaceId && (
                  <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-blue-600 dark:bg-slate-700 dark:text-[#D9F42B]">
                    Current
                  </span>
                )}
              </div>
              <ul className="space-y-0.5">
                {group.routes.map((route) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  const Icon = route.icon;
                  const isActive = index === activeIndex;
                  return (
                    <li key={route.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => selectRoute(route)}
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                          isActive
                            ? 'bg-blue-50 text-foreground dark:bg-slate-800'
                            : 'text-foreground-secondary hover:bg-background'
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                            isActive
                              ? 'bg-accent-primary text-white'
                              : 'bg-background text-foreground-muted ring-1 ring-border'
                          }`}
                        >
                          <Icon style={{ fontSize: 14 }} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-semibold text-foreground">
                            {route.label}
                          </span>
                          <span className="mt-0.5 block truncate text-[11px] text-foreground-muted">
                            {route.groupLabel}
                          </span>
                        </span>
                        <span className="hidden max-w-[140px] truncate text-[10px] text-foreground-muted sm:block">
                          {route.path.replace(/^\/v2/, '')}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-4 py-2 text-[11px] text-foreground-muted">
          <span>
            <kbd className="rounded bg-background px-1 py-0.5 ring-1 ring-border">↑</kbd>{' '}
            <kbd className="rounded bg-background px-1 py-0.5 ring-1 ring-border">↓</kbd> navigate
          </span>
          <span>
            <kbd className="rounded bg-background px-1 py-0.5 ring-1 ring-border">↵</kbd> open
          </span>
          <span className="ml-auto">
            {grouped.length} workspaces · {allRoutes.length} routes
          </span>
        </div>
      </div>
    </div>
  );
};

export default RouteCommandPalette;
